import { useEffect, useState } from "react";
import { Download, Check, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { saveForOffline, isDownloaded, removeDownload, getOfflineBlobUrl, type SaveOfflineInput } from "@/lib/offline/downloads";

type Props = Omit<SaveOfflineInput, "onProgress"> & {
  className?: string;
  /** Optional: when offline blob is ready, hand the consumer a usable URL. */
  onOfflineReady?: (blobUrl: string) => void;
  compact?: boolean;
};

export function DownloadButton({ id, kind, title, url, userId, updatedAt, onOfflineReady, className = "", compact = false }: Props) {
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void isDownloaded(id).then((v) => { if (!cancelled) setSaved(v); });
    return () => { cancelled = true; };
  }, [id]);

  async function handleSave() {
    setBusy(true);
    setProgress(0);
    try {
      await saveForOffline({ id, kind, title, url, userId, updatedAt, onProgress: setProgress });
      setSaved(true);
      toast.success("Saved for offline use");
      if (onOfflineReady) {
        const u = await getOfflineBlobUrl(id);
        if (u) onOfflineReady(u);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save offline");
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    setBusy(true);
    try {
      await removeDownload(id);
      setSaved(false);
      toast.success("Removed from device");
    } finally { setBusy(false); }
  }

  const base = "inline-flex items-center gap-1.5 rounded-full border text-xs font-medium transition disabled:opacity-50";
  const size = compact ? "px-2.5 py-1" : "px-3 py-1.5 text-sm";

  if (saved) {
    return (
      <button onClick={handleRemove} disabled={busy} className={`${base} ${size} border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 ${className}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        Available offline
        <Trash2 className="ml-1 h-3 w-3 opacity-60" />
      </button>
    );
  }

  return (
    <button onClick={handleSave} disabled={busy} className={`${base} ${size} border-border bg-background hover:bg-accent ${className}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
      {busy ? `Saving… ${progress}%` : "Save offline"}
    </button>
  );
}
